// Typed client for the Studio analytics REST API (/v1/studio/analytics/*).
// Built against the backend contract; every endpoint takes the same Bearer auth
// as the guides client via apiRequest.

import { apiRequest } from "./http";
import type { EngineName } from "./voiceEngines";

export type CallMode = "voice" | "text";
// Traffic source: real app calls vs Studio voice-bench test calls. The server
// defaults to "app" when the param is omitted.
export type CallSource = "app" | "studio_bench";
export type SentimentLabel = "positive" | "neutral" | "negative" | "mixed";
// "screened": too short for full analysis — only the safety screen ran.
export type AnalysisStatus = "pending" | "complete" | "failed" | "skipped" | "screened";
// What the user actually did on a connection (typed chats ride the voice
// pipeline, so `mode` alone can't tell).
export type InputMode = "voice" | "text" | "mixed";
// How conversation-level rates are averaged: every conversation/reply once
// ("pooled") or per user first ("user" — heavy users don't dominate).
export type OverviewWeight = "pooled" | "user";

export interface LatencyBucket {
  p50: number;
  p95: number;
  max: number;
  count: number;
}

// Bucket keys are snake_case in the payload (backend metric names).
export interface LatencyStats {
  first_audio_ms?: LatencyBucket | null;
  turn_ms?: LatencyBucket | null;
  // Raw audio-path byte counters (voice rows closed normally; absent on
  // older rows). Either above 0 means the call should have a recording.
  audio?: { mic_bytes?: number; agent_audio_bytes?: number } | null;
}

export interface CallRecord {
  id: string;
  uid: string;
  sessionId: string;
  guideId: string;
  guideName: string;
  guideVersion: number;
  mode: CallMode;
  source: CallSource;
  startedAt: string;
  endedAt: string | null;
  durationMs: number;
  userMsgCount: number;
  modelMsgCount: number;
  endReason: string | null;
  hasRecording: boolean;
  recordingDurationS: number | null;
  latencyStats: LatencyStats | null;
  toolCallCount: number;
  toolMsTotal: number;
  sentimentScore: number | null;
  sentimentLabel: SentimentLabel | null;
  emotions: string[];
  topics: string[];
  callSummary: string | null;
  flags: string[];
  analysisStatus: AnalysisStatus;
  // A row is one socket; conversationId groups a conversation's reconnects.
  conversationId?: string | null;
  // Phoenix trace of the call; null when tracing was off.
  traceId?: string | null;
  inputMode?: InputMode | null;
  talkOverCount?: number | null;
  interruptionCount?: number | null;
  fragmentedUserTurns?: number | null;
  replyWordsP50?: number | null;
  questionEndRate?: number | null;
  adviceRequests?: number | null;
  adviceDeflections?: number | null;
  // Voice engine (null on older rows and text episodes): what the resolver
  // chose, its experiment arm and profile, and why it fell back mid-call.
  voiceEngine?: EngineName | null;
  voiceEngineArm?: string | null;
  voiceEngineProfile?: string | null;
  voiceEngineExperiment?: string | null;
  voiceEngineFallback?: string | null;
}

export interface AnalyticsOverview {
  calls: number;
  uniqueUsers: number;
  avgDurationMs: number | null;
  avgSentiment: number | null;
  avgUserMsgCount: number | null;
  avgEvalScore: number | null;
  flaggedCount: number;
  liveNow: number;
  // Engagement / intervention funnel (Aug 2026 clinical feedback metrics)
  earlyDropCount: number;
  earlyDropRate: number | null;
  analyzedCalls: number;
  extractedCalls: number;
  interventionCalls: number;
  interventionRate: number | null;
  disengagedAfterInterventionCount: number;
  disengagedAfterInterventionRate: number | null;
  // Conversation level: the fields above count sockets (~4.5 per real
  // conversation); these regroup them. `weight` echoes the averaging used.
  weight: OverviewWeight;
  conversations: number;
  connectionsPerConversation: number | null;
  medianUserTurnsPerConversation: number | null;
  conversationEarlyDropCount: number;
  conversationEarlyDropRate: number | null;
  typedShare: number | null;
  conversationsByInputMode: Partial<Record<InputMode, number>>;
  // Deterministic conversation-health rates
  healthConnections: number;
  talkOverRate: number | null;
  interruptionRate: number | null;
  fragmentedTurnRate: number | null;
  questionEndRate: number | null;
  adviceRequests: number;
  adviceDeflectionRate: number | null;
  medianReplyWordsByInputMode: Partial<Record<InputMode, number | null>>;
}

export interface GuideAnalyticsRow {
  guideId: string;
  guideName: string;
  calls: number;
  uniqueUsers: number;
  avgDurationMs: number | null;
  avgSentiment: number | null;
  lastCallAt: string | null;
  // Rubric quality. evalCount is how many of `calls` the judge scored — a
  // null avg means "not sampled yet", not "bad".
  avgEvalScore: number | null;
  evalCount: number;
}

export interface TimeseriesPoint {
  date: string; // YYYY-MM-DD
  calls: number;
  avgDurationMs: number | null;
  avgSentiment: number | null;
}

export interface TranscriptEntry {
  role: "user" | "model";
  text: string;
  createdAt: string;
  interruption?: {
    interruptedAtMs?: number;
    heardRatio?: number;
    intendedText?: string;
  } | null;
}

export interface AnalyticsRangeParams {
  from?: string; // ISO datetime
  to?: string; // ISO datetime
  input_mode?: InputMode; // what the user did; omitted = all (incl. rows with none)
  source?: CallSource | "all"; // omitted = server default ("app")
  // Voice rows that ran this engine (older rows count as the cascade; text
  // episodes never match); omitted = every row.
  engine?: EngineName;
}

export interface CallsQuery extends AnalyticsRangeParams {
  guideId?: string;
  uid?: string;
  sentiment?: SentimentLabel;
  flagged?: boolean;
  // Exact matches on the voice engine columns (Voice engines → Results links).
  experiment?: string;
  arm?: string;
  fallback?: string;
  limit?: number;
  cursor?: string;
}

// ---- Voice engines → Results (GET /studio/analytics/voice-engines). Mirrors
// app/models/voice_engine_results.py; null means no data, never zero.

export interface RateRange {
  low: number;
  high: number;
  users: number;
}

// Each user's own value, averaged; `users` had a value.
export interface UserMean {
  value: number | null;
  users: number;
  ci: RateRange | null;
}

export interface ArmResults {
  arm: string | null; // null: the rest (outside every arm, on the cascade)
  engine: EngineName | null;
  engines: Record<string, number>;
  profiles: Record<string, number>;
  users: number;
  calls: number; // conversations (reconnects folded)
  connections: number; // sockets
  lowSample: boolean;
  topUserShare: number | null;
  longCallRate: UserMean;
  medianCallMinutes: number | null;
  firstAudio: { p50Ms: number | null; p95Ms: number | null; connections: number };
  interruptionsPerCall: UserMean;
  talkOversPerCall: UserMean;
  healthCalls: number;
  fallback: { rate: UserMean; calls: number; reasons: Record<string, number> } | null;
  cost: { perMinute: number; usd: number; minutes: number; connections: number } | null;
  sentiment: { positiveShare: UserMean; meanScore: number | null; connections: number };
  crisis: { judged: number; missedOrHarmful: number; partial: number };
}

export interface ExperimentSeen {
  key: string;
  users: number;
  connections: number;
  firstCallAt: string | null;
  lastCallAt: string | null;
}

export interface VoiceEngineResults {
  experiment: string | null;
  experiments: ExperimentSeen[];
  language: string | null;
  languages: { language: string; users: number; connections: number }[];
  firstCallAt: string | null;
  lastCallAt: string | null;
  capped: boolean;
  lowSampleUsers: number;
  longCallSeconds: number;
  crossArmUsers: number;
  arms: ArmResults[];
}

export interface VoiceEngineResultsQuery {
  experiment?: string; // omitted: the newest experiment with calls
  from?: string;
  to?: string;
  language?: string; // "en", "vi", … or "unknown"; omitted: every language
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : "";
}

export const getOverview = (params: AnalyticsRangeParams & { weight?: OverviewWeight }) =>
  apiRequest<AnalyticsOverview>(`/studio/analytics/overview${qs({ ...params })}`);

export const getGuideAnalytics = (params: AnalyticsRangeParams) =>
  apiRequest<{ items: GuideAnalyticsRow[] }>(`/studio/analytics/guides${qs({ ...params })}`);

export const getTimeseries = (params: AnalyticsRangeParams & { guideId?: string }) =>
  apiRequest<{ items: TimeseriesPoint[] }>(`/studio/analytics/timeseries${qs({ ...params })}`);

export const listCalls = (params: CallsQuery) =>
  apiRequest<{ items: CallRecord[]; nextCursor: string | null }>(
    `/studio/analytics/calls${qs({ ...params })}`,
  );

export const getVoiceEngineResults = (params: VoiceEngineResultsQuery) =>
  apiRequest<VoiceEngineResults>(`/studio/analytics/voice-engines${qs({ ...params })}`);

export const getCall = (id: string) =>
  apiRequest<{ call: CallRecord; transcript: TranscriptEntry[] }>(
    `/studio/analytics/calls/${encodeURIComponent(id)}`,
  );

export const getRecordingUrl = (id: string) =>
  apiRequest<{ url: string; expiresAt: string }>(
    `/studio/analytics/calls/${encodeURIComponent(id)}/recording-url`,
  );
